import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];
const LAST_ACTIVITY_KEY = "accrued_last_activity";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=unauth, obj=auth
  const [account, setAccount] = useState(null); // practice account status
  const [locked, setLocked] = useState(false);
  const timerRef = useRef(null);

  const loadAccount = useCallback(async (u) => {
    if (!u || typeof u !== "object" || u.role === "superadmin") {
      setAccount(null);
      return;
    }
    try {
      const { data } = await api.get("/account/status");
      setAccount(data);
    } catch {
      setAccount(null);
    }
  }, []);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("accrued_token");
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      loadAccount(data.user);
      // Re-lock if idle window elapsed while the tab was closed/backgrounded.
      const timeoutMin = data.user?.inactivity_timeout || 0;
      if (timeoutMin > 0) {
        const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0);
        if (last && Date.now() - last > timeoutMin * 60 * 1000) setLocked(true);
      }
    } catch {
      localStorage.removeItem("accrued_token");
      setUser(false);
    }
  }, [loadAccount]);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = (userObj, token) => {
    if (token) localStorage.setItem("accrued_token", token);
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    setLocked(false);
    setUser(userObj);
    loadAccount(userObj);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("accrued_token");
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    setLocked(false);
    setUser(false);
  };

  const unlock = useCallback(() => {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    setLocked(false);
  }, []);

  // Inactivity timer — only when authenticated and a timeout is configured.
  useEffect(() => {
    const timeoutMin = user && typeof user === "object" ? user.inactivity_timeout || 0 : 0;
    if (!user || typeof user !== "object" || timeoutMin <= 0 || locked) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }
    const limitMs = timeoutMin * 60 * 1000;
    const reset = () => {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setLocked(true), limitMs);
    };
    reset();
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, reset, { passive: true }));
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, reset));
    };
  }, [user, locked]);

  return (
    <AuthContext.Provider value={{ user, account, login, logout, refresh: loadMe, refreshAccount: () => loadAccount(user), locked, unlock }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
